import { v4 as uuidv4 } from "uuid";

import { type ICol } from "../../../interfaces";
/**
 * Generates a comments input object with predefined properties.
 *
 * @returns An object representing a comments input field.
 */
const generateCommentsInput = (searchEntries?: string) => ({
  name: "COMMENTS",
  unit: "text",
  placeholder: "Comments",
  full: true,
  timestamp: Date.now(),
  wasGenerated: true,
  ...(searchEntries && { searchEntries }),
});
/**
 * Generates a comments row object with a unique identifier and predefined properties.
 *
 * @returns An object representing a comments row.
 */
const generateCommentsRow = (searchEntries?: string) => ({
  cols: {
    [uuidv4()]: generateCommentsInput(searchEntries),
  },
  wasGenerated: true,
  symbol: uuidv4(),
});
/**
 * Parses rows and adds a comments row to each or a specific row based on the provided index.
 *
 * @param rows - An array of rows, where each row contains columns and optional subrows.
 * @param rowIndex - An optional index specifying which row to modify. If undefined, all rows are modified.
 * @returns An array of rows with added comments rows.
 */
export const parseRowsIntoDoubled = (
  rows: Array<{ cols: ICol[]; subrows?: ICol[] }>,
  rowIndex?: number,
) => {
  const parseRow = (row: any) => {
    if (row.cols) {
      return {
        subrows: [{ cols: row.cols }, { ...generateCommentsRow() }],
      };
    }
    if (row.subrows) {
      const firstComment = row.subrows
        .map((subrow: any) =>
          Object.values(subrow.cols).find(
            (cell: any) => cell.name === "COMMENTS" && !cell.wasGenerated,
          ),
        )
        .filter((v: any) => typeof v !== "undefined")[0];

      const searchEntries =
        firstComment && firstComment.searchEntries
          ? firstComment.searchEntries
          : undefined;

      return {
        subrows: [...row.subrows, { ...generateCommentsRow(searchEntries) }],
      };
    }
    return row;
  };
  // if rowIndex is not provided, add comments to all rows
  if (rowIndex === undefined) return rows.map(parseRow);
  // else add comments to the row at rowIndex
  return rows.map((row, idx) => {
    if (rowIndex === idx) return parseRow(row);
    return row;
  });
};
/**
 * Checks if a row contains any generated cells.
 *
 * @param row - A row object to check for generated cells.
 * @returns A boolean indicating whether the row contains generated cells.
 */
export const checkIfRowContainsGeneratedCells = (row: any) =>
  row.wasGenerated ||
  Object.values(row.cols).some((cell: any) => cell.wasGenerated);

/**
 * Finds the maximum number of generated rows within the provided rows.
 *
 * @param rows - An array of rows to analyze.
 * @returns The maximum number of generated rows found.
 */
const findMaxAmountOfGeneratedRows = (rows: any[]) => {
  let maxGeneratedRows = 0;
  rows.forEach(row => {
    if (row.subrows) {
      let localCount = 0;
      row.subrows.forEach((subrow: any) => {
        if (checkIfRowContainsGeneratedCells(subrow)) {
          localCount++;
        }
      });
      if (localCount >= maxGeneratedRows) {
        maxGeneratedRows = localCount;
      }
    }
  });
  return maxGeneratedRows;
};
/**
 * Parses headers and adds generated comments headers based on the number of generated rows.
 *
 * @param rows - An array of rows to determine the number of generated rows.
 * @param headers - An array of header columns to modify.
 * @returns An array of headers with added generated comments headers.
 */
export const parseHeadersIntoDoubled = (
  rows: Array<{
    cols?: ICol[];
    subrows?: Array<{ cols: ICol[]; wasGenerated?: boolean }>;
  }>,
  headers: ICol[],
) => {
  const maxGeneratedRows = findMaxAmountOfGeneratedRows(rows);
  let headersWithoutGeneratedComments: any = headers.filter(
    el =>
      (Array.isArray(el) && el.some(e => !e.wasGenerated)) ||
      !Array.isArray(el),
  );
  for (let i = 0; i < maxGeneratedRows; i++) {
    if (!Array.isArray(headers[0])) {
      headersWithoutGeneratedComments = [
        headersWithoutGeneratedComments,
        [generateCommentsInput()],
      ];
    } else {
      headersWithoutGeneratedComments = [
        ...headersWithoutGeneratedComments,
        [generateCommentsInput()],
      ];
    }
  }
  return headersWithoutGeneratedComments;
};
